import { Injectable, NgZone, inject, signal } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SingleMapService {
  private readonly ngZone = inject(NgZone);

  private mapInstance: google.maps.Map | null = null;
  private mapElement: HTMLElement | null = null; // The actual <div> map binds to
  private persistentHostElement: HTMLElement | null = null; // Hidden host in AppComponent
  private isInitialized = false;
  // signal so effects re-run once the map exists (Angular 19+ runs component effects before parent view init)
  private readonly initialized = signal(false);
  private isAttached = false; // Track if currently attached to a component's view

  // Ownership stack to manage which component currently owns the map
  private readonly ownerStack: { ownerId: string; container: HTMLElement; options?: google.maps.MapOptions }[] = [];

  // Store listener cleanup functions or details
  private readonly controlListeners: { element: EventTarget; type: string; listener: EventListenerOrEventListenerObject }[] = [];
  // Store references to control containers
  private controlContainers: { [key: string]: HTMLElement } = {};

  // Emits the current top ownerId (or null if detached to host)
  private readonly ownerSubject = new BehaviorSubject<string | null>(null);
  currentOwnerId$ = this.ownerSubject.asObservable();

  readonly fullScreenImage = `assets/google-map-assets/icons/EnterFullScreen.svg`;
  readonly exitFullScreenImage = `assets/google-map-assets/icons/ExitFullScreen.svg`;
  readonly satelliteImage = `assets/google-map-assets/icons/Satellite-control-image.png`;

  initialMapOptions: google.maps.MapOptions = {
    center: {
      lat: 37.4,
      lng: -95.38,
    },
    zoom: 4,
    clickableIcons: false,
    disableDoubleClickZoom: true,
    streetViewControl: true,
    mapTypeControl: false,
    disableDefaultUI: true,
    mapId: '6a2150d7d0e6b1dc',
    cameraControl: false,
    zoomControl: true,
    fullscreenControl: true,
  };

  /**
   * Creates the single map instance. Call ONCE from AppComponent.
   * @param hostElement The hidden div in AppComponent to hold the map when not in use.
   */
  initializeMap(hostElement: HTMLElement): void {
    if (this.isInitialized) return;

    this.persistentHostElement = hostElement;

    this.mapElement = document.createElement('div');
    this.mapElement.style.width = '100%';
    this.mapElement.style.height = '100%';

    this.ngZone.runOutsideAngular(() => {
      // Run map creation outside Angular zone
      try {
        this.mapInstance = new google.maps.Map(this.mapElement!, this.initialMapOptions);
        this.isInitialized = true;
        // Initially, keep the map element hidden in the persistent host
        if (this.mapElement) this.persistentHostElement?.appendChild(this.mapElement);

        // --- Initialize Custom Controls AFTER map instance exists ---
        this.initializeControlContainers();
        this.initializeControls(this.initialMapOptions);
      } catch (error) {
        console.error('Single Map Service: Error initializing map:', error);
      }
    });
    this.initialized.set(this.isInitialized);
  }

  /**
   * Attaches the map to a component's target container.
   * @param targetContainer The component's div where the map should be displayed.
   * @param options Component-specific map options to apply.
   * @returns The map instance or null if not initialized.
   */
  attachMap(targetContainer: HTMLElement, options?: google.maps.MapOptions): google.maps.Map | null {
    if (!this.isInitialized || !this.mapElement || !this.mapInstance) {
      console.error('Map Singleton Service: Map not initialized, cannot attach.');
      return null;
    }
    targetContainer.appendChild(this.mapElement); // Move map's div to component's div
    this.isAttached = true;

    const defaultDisabledControls = {
      disableDefaultUI: true,
      // Ensure default controls corresponding to custom ones remain logically disabled for the API
      zoomControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    };

    if (options) {
      this.mapInstance.setOptions({
        ...options,
        ...defaultDisabledControls,
      });
    } else {
      this.mapInstance.setOptions({
        ...this.initialMapOptions,
        ...defaultDisabledControls,
      });
    }
    // IMPORTANT: Trigger resize after attaching to ensure it renders correctly
    setTimeout(() => google?.maps?.event?.trigger?.(this.mapInstance!, 'resize'), 10);
    return this.mapInstance;
  }

  /**
   * Detaches the map from its current container and moves it back to the hidden host.
   * Component should clean its own markers/listeners BEFORE calling this.
   */
  detachMap(): void {
    if (!this.isInitialized || !this.mapElement || !this.persistentHostElement || !this.isAttached) {
      return; // Not initialized or not attached, nothing to detach
    }

    // Clear Street View before detaching to prevent it from persisting
    this.clearStreetView();

    // Move map's div back to the persistent host to hide it
    this.persistentHostElement.appendChild(this.mapElement);
    this.isAttached = false;
    this.ownerSubject.next(null);
    // Reset map options to default? Optional.
    // this.mapInstance?.setOptions(this.initialOptions);
  }

  /**
   * Acquire ownership of the single map instance and attach it to the provided container.
   * Manages a stack of owners to correctly restore the previous attachment on release.
   */
  acquireMap(ownerId: string, targetContainer: HTMLElement, options?: google.maps.MapOptions): google.maps.Map | null {
    if (!this.isInitialized || !this.mapElement || !this.mapInstance) {
      console.error('Map Singleton Service: Map not initialized, cannot acquire.');
      return null;
    }

    const currentTop = this.ownerStack.at(-1);

    // If the same owner re-acquires (e.g., re-render), update container/options and reattach
    if (currentTop?.ownerId === ownerId) {
      currentTop.container = targetContainer;
      currentTop.options = options ?? currentTop.options;
      return this.attachMap(targetContainer, currentTop.options);
    }

    // Push new owner and attach
    this.ownerStack.push({ ownerId, container: targetContainer, options });
    const map = this.attachMap(targetContainer, options);
    this.ownerSubject.next(ownerId);
    return map;
  }

  /**
   * Release ownership of the map. If releasing the top owner, reattach to the previous owner
   * or detach to the hidden host when no owners remain. If releasing a non-top owner, simply
   * remove it from the stack.
   */
  releaseMap(ownerId: string): void {
    const stackSize = this.ownerStack.length;
    if (stackSize === 0) {
      // No owners known; ensure detached
      this.detachMap();
      return;
    }

    const top = this.ownerStack[stackSize - 1];
    if (top.ownerId !== ownerId) {
      // Remove from middle if exists; no reattach needed
      const idx = this.ownerStack.findIndex(o => o.ownerId === ownerId);
      if (idx >= 0) this.ownerStack.splice(idx, 1);
      return;
    }

    // Clear Street View before releasing to prevent it from persisting
    this.clearStreetView();

    // Pop current owner
    this.ownerStack.pop();
    const previous = this.ownerStack.at(-1);
    if (previous) {
      this.attachMap(previous.container, previous.options);
      // Ensure correct rendering after re-attachment
      setTimeout(() => google?.maps?.event?.trigger?.(this.mapInstance!, 'resize'), 0);
      this.ownerSubject.next(previous.ownerId);
    } else {
      this.detachMap();
    }
  }

  /** Get the underlying google map instance for components to rebind listeners when re-activated */
  getMapInstance(): google.maps.Map | null {
    return this.mapInstance;
  }

  // Helper to check readiness
  isMapInitialized(): boolean {
    return this.initialized();
  }

  // --- Control Initialization Logic (Adapted from Directive) ---

  private initializeControlContainers(): void {
    if (!this.mapInstance) return;
    const positions = [
      { key: 'RIGHT_TOP', position: google.maps.ControlPosition.RIGHT_TOP },
      { key: 'RIGHT_BOTTOM', position: google.maps.ControlPosition.RIGHT_BOTTOM },
      { key: 'LEFT_TOP', position: google.maps.ControlPosition.LEFT_TOP },
      { key: 'LEFT_BOTTOM', position: google.maps.ControlPosition.LEFT_BOTTOM },
    ];
    positions.forEach(p => {
      const container = document.createElement('div');
      // Add classes for potential styling hooks
      container.classList.add(`st-google-map-control-container`, `st-google-map-${p.key.toLowerCase().replace('_', '-')}-container`);
      this.mapInstance!.controls[p.position].push(container);
      this.controlContainers[p.key] = container;
    });
  }

  private initializeControls(options: google.maps.MapOptions): void {
    if (!this.mapInstance) return;
    // Add controls based on the provided options
    if (options.zoomControl) this.addZoomControls();
    if (options.fullscreenControl) this.addFullScreenControl();
  }

  private addZoomControls(): void {
    const container = this.controlContainers['RIGHT_BOTTOM']; // Example position
    if (!container || !this.mapInstance) return;

    const zoomControls = document.createElement('div');
    zoomControls.classList.add('st-google-map-zoom-controls'); // Use same class for styling

    const zoomIn = document.createElement('button');
    zoomIn.classList.add('st-google-map-zoom-in-control'); // Use same class
    zoomIn.type = 'button'; // Good practice for buttons
    zoomIn.textContent = '+';
    // Use helper to add listener and store it for cleanup
    this._addManagedListener(zoomIn, 'click', () => {
      const currentZoom = this.mapInstance?.getZoom();
      if (currentZoom !== undefined) this.mapInstance?.setZoom(currentZoom + 1);
    });

    const zoomOut = document.createElement('button');
    zoomOut.classList.add('st-google-map-zoom-out-control'); // Use same class
    zoomOut.type = 'button';
    zoomOut.textContent = '-';
    this._addManagedListener(zoomOut, 'click', () => {
      const currentZoom = this.mapInstance?.getZoom();
      if (currentZoom !== undefined) this.mapInstance?.setZoom(currentZoom - 1);
    });

    zoomControls.appendChild(zoomIn);
    zoomControls.appendChild(zoomOut);
    container.appendChild(zoomControls);
  }

  private addFullScreenControl(): void {
    const container = this.controlContainers['RIGHT_TOP']; // Example position
    // Fullscreen needs the map *container* element, which is this.mapElement
    if (!container || !this.mapElement) return;

    const fullScreenButton = document.createElement('button');
    fullScreenButton.classList.add('st-google-map-full-screen-control'); // Use same class
    fullScreenButton.type = 'button';
    const img = this._createImage(this.fullScreenImage, 'FullScreen', 'st-google-map-full-screen-control-img');
    fullScreenButton.appendChild(img);

    this._addManagedListener(fullScreenButton, 'click', () => {
      const elementToMakeFullscreen = this.mapElement!; // The map's container div
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
      } else {
        elementToMakeFullscreen.requestFullscreen?.().catch(err => console.error('FS Error', err));
      }
    });

    // Listener for Esc key or browser UI exit
    const fullscreenChangeHandler = () => {
      if (document.fullscreenElement === this.mapElement) {
        img.src = this.exitFullScreenImage;
      } else {
        img.src = this.fullScreenImage;
      }
    };
    // Add listener to document, store for cleanup
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    this.controlListeners.push({ element: document, type: 'fullscreenchange', listener: fullscreenChangeHandler });

    container.appendChild(fullScreenButton);
  }

  // Helper to create image elements (adapted from directive)
  private _createImage(src: string, alt: string, id: string): HTMLImageElement {
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt;
    img.id = id; // ID might not be unique if service used multiple times? Consider class instead.
    img.loading = 'lazy';
    img.classList.add('st-google-map-control-img'); // Add class for styling
    return img;
  }

  // Helper to add listeners and store them for removal
  private _addManagedListener(element: EventTarget, type: string, listener: EventListenerOrEventListenerObject): void {
    element.addEventListener(type, listener);
    this.controlListeners.push({ element, type, listener });
  }

  /**
   * Exits Street View by setting visibility to false, just like the native "Exit Street View" button.
   * This is the simplest and most reliable way to handle Street View state.
   */
  private clearStreetView(): void {
    if (!this.mapInstance) return;

    try {
      const streetView = this.mapInstance.getStreetView();
      console.log('street view: ', streetView);

      if (streetView?.getVisible()) {
        console.log('Street view is visible, exiting now.', streetView);

        streetView.setVisible(false);
      }
    } catch (error) {
      console.warn('SingleMapService: Error clearing Street View:', error);
    }
  }

  /**
   * Public method to exit Street View - can be called by components when needed
   */
  exitStreetView(): void {
    console.log('Exit street view');

    this.clearStreetView();
  }
}
